import { FC, Fragment, Suspense, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Box } from "@mui/material";
import RouteFallback from "RouteFallback";
import MainHeader from "Components/Header";
import HeadProvider from "Context/HeadContext";
import Notification from "Service/Notification";
import { ListInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { useSelector, useDispatch } from "react-redux";
import { Client } from "stompjs";
import NotificationFormatter from "Utils/Notification.Formatter";
import {
  addNotificationList,
  addOrgDropList,
  setUnreadNotificationBellCount,
} from "Redux/List/Action";
import { NotificationType } from "DataTypes/Notification";
import { Logout } from "Utils/common";
import PracticeApi from "Service/Practice.api";
import { FormatDropdownData } from "Utils/DataFormatter";
import {
  updateRPMAlertCount,
  updateTimezoneMonthEndTime
} from "Redux/User/Action";
import SettingsApi from "Service/Settings.api";
import SettingsFormatter from "Utils/Settings.Formatter";
import { store } from "Redux/Store";
import {
  BELL_UNREAD_DAYS,
  BELL_UNREAD_LIMIT,
} from "Constant/NotificationBell";

const compareNotificationDatetime = (
  left: NotificationType,
  right: NotificationType
): number => {
  const leftTime = left.datetime || "";
  const rightTime = right.datetime || "";
  return rightTime.localeCompare(leftTime);
};

const mergeBellNotifications = (
  existing: NotificationType[],
  incoming: NotificationType[]
): NotificationType[] => {
  const byId = new Map<number, NotificationType>();
  existing.forEach((item) => {
    if (item.id != null) {
      byId.set(item.id, item);
    }
  });
  incoming.forEach((item) => {
    if (item.id != null) {
      byId.set(item.id, item);
    }
  });
  return Array.from(byId.values())
    .filter((item) => !item.isNotificationRead)
    .sort(compareNotificationDatetime)
    .slice(0, BELL_UNREAD_LIMIT);
};

const Home: FC = () => {
  const dispatch = useDispatch();
  let stompClient: Client;
  const { myInfo, timezoneMonthEndTimeArr }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const [notification, setNotification] = useState<NotificationType | null>(
    null
  );
  const { orgDropList }: ListInitialStateType = useSelector(
    (state: any) => state?.list
  );
  const headers = { Authorization: myInfo.id };

  useEffect(() => {
    ConnectClient();
    GetUserOrgList();
    CallTimezoneApi();
  }, []);

  useEffect(() => {
    if (myInfo?.id) {
      LoadUnreadNotifications();
      LoadUnreadNotificationBellCount();
    }
  }, [myInfo?.id]);

  const CallTimezoneApi: () => void = async () => {
    if (timezoneMonthEndTimeArr.length == 0) {
      const res = await SettingsApi.TimezoneEndMonthDateTime();
      if (res.success) {
        const data = SettingsFormatter.TimezoneDateTimeList(res?.data);
        dispatch(updateTimezoneMonthEndTime(data));
      }
    }
  }

  const ConnectClient: () => void = () => {
    if (!stompClient?.connected) {
      stompClient = Notification.list();
      stompClient.connect(headers, SubscribeClient, (error: any) => {
        console.error("WebSocket connection error:", error);
      });
    }
  };

  const SubscribeClient: () => void = () => {
    if (stompClient?.connected) {
      stompClient.subscribe("/user/specific", async (msg) => {
        const receivedMsg = JSON.parse(msg?.body);
        const formattedMsg = NotificationFormatter.Obj(receivedMsg);
        setNotification(formattedMsg);
      });
      stompClient.subscribe("/user/alert", async (msg) => {
        const receivedMsg = JSON.parse(msg?.body);
        dispatch(updateRPMAlertCount(parseInt(receivedMsg)));
      });
    } else DisconnectClient();
  };

  useEffect(() => {
    notification && setData(notification);
  }, [notification]);

  const LoadUnreadNotificationBellCount: () => Promise<void> = async () => {
    const count = await Notification.count();
    dispatch(setUnreadNotificationBellCount(count));
  };

  const LoadUnreadNotifications: () => Promise<void> = async () => {
    if (!myInfo?.id) return;
    const res = await Notification.search({
      data: {
        receiverUserId: myInfo.id,
        isNotificationRead: false,
        createdWithinDays: BELL_UNREAD_DAYS,
      },
      limit: BELL_UNREAD_LIMIT,
      offset: 0,
    });
    if (res?.success) {
      const fetched = NotificationFormatter.Arr(res.data);
      const existing =
        (store.getState().list?.notificationList as NotificationType[]) || [];
      dispatch(
        addNotificationList(mergeBellNotifications(existing, fetched))
      );
    }
  };

  const setData = (notification: NotificationType): void => {
    if (notification.isNotificationRead) {
      return;
    }
    const existing =
      (store.getState().list?.notificationList as NotificationType[]) || [];
    dispatch(
      addNotificationList(mergeBellNotifications(existing, [notification]))
    );
    LoadUnreadNotificationBellCount();
  };

  const DisconnectClient: () => void = () => {
    if (stompClient?.connected) {
      stompClient?.disconnect(() => {
        console.log("Client disconnected", headers);
      });
    }
    Logout();
  };

  const GetUserOrgList: () => void = async () => {
    if (orgDropList.length == 0) {
      const userPracticeRes = await PracticeApi.UserPracticeList({
        data: {},
        limit: 25,
        offset: 0,
      });
      if (userPracticeRes?.success) {
        const newArr = FormatDropdownData(
          userPracticeRes?.data,
          "practiceId",
          "practiceName",
          "practiceId"
        );
        dispatch(addOrgDropList([...newArr]));
      }
    }
  };

  return (
    <Fragment>
      <HeadProvider>
        <MainHeader />
        <Box sx={{ ...sxMain, px: 1 }}>
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </Box>
      </HeadProvider>
    </Fragment>
  );
};
export default Home;

const sxMain = {
  height: "92%",
  overflow: "auto",
  backgroundColor: "bg.main",
};
